/**
 * SupplierQuoteCaptureMulti · iter303ak-fix26 — Quote Sent · T25 · A55
 * ────────────────────────────────────────────────────────────────────
 * Accordion — one row per supplier the request was sent to. Enter their
 * returned quote inline: job_ref, amount (NZD), downtime_days, notes.
 * Multi-entry — quotes can trickle in over days. Operator continues
 * once at least one quote is captured (partial is OK — winner pick is
 * next step).
 *
 * Writes:
 *   safety_supplier_quotes = {
 *     [supplier_id]: { supplier_name, email, job_ref, amount, downtime_days, notes, entered_at }
 *   }
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, CheckCircle2, DollarSign, Clock } from 'lucide-react';

export default function SupplierQuoteCaptureMulti({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};

  const requestedSuppliers = useMemo(() => {
    // Prefer send-state (only successfully sent get captured); fall back to selection.
    const sendState = capturedData?.height_safety_send_state?.send_state || capturedData?.height_safety_send_state || {};
    const selection = capturedData?.height_safety_supplier_selection?.selection || capturedData?.height_safety_supplier_selection || {};
    const rows = [];
    for (const [sid, sel] of Object.entries(selection)) {
      if (!sel?.include) continue;
      const ss = sendState[sid] || {};
      rows.push({
        supplier_id: sid,
        name: sel.name || 'Supplier',
        email: sel.email || '',
        sent_at: ss.sent_at || null,
      });
    }
    return rows;
  }, [capturedData]);

  const [quotes, setQuotes] = useState(() => value?.quotes || {});

  const updateQuote = (sid, patch) => {
    setQuotes(prev => ({
      ...prev,
      [sid]: { ...(prev[sid] || {}), ...patch },
    }));
  };

  useEffect(() => {
    if (!onChange) return;
    // Only include quotes where at least amount OR job_ref was entered.
    const clean = {};
    for (const [sid, q] of Object.entries(quotes)) {
      if (q && (q.amount || q.job_ref)) {
        clean[sid] = { ...q, entered_at: q.entered_at || new Date().toISOString() };
      }
    }
    onChange({ quotes: clean });
  }, [quotes, onChange]);

  const capturedCount = Object.values(quotes).filter(q => q && (q.amount || q.job_ref)).length;
  const valid = capturedCount > 0;

  return (
    <div className="space-y-4" data-testid="wf-supplier-quote-capture-multi">
      <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4">
        <div className="flex items-start gap-3">
          <ClipboardList className="w-5 h-5 mt-0.5 shrink-0 text-teal-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-teal-900">Capture Supplier Quotes</div>
            <div className="text-sm mt-1 text-teal-800">
              {params.prompt || 'Enter each supplier\'s returned quote as they reply. Partial capture is fine — you can come back to this step.'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-teal-600 font-semibold">Captured</div>
            <div className="text-lg font-bold text-teal-900 leading-none">{capturedCount}<span className="text-xs text-teal-500 font-normal"> / {requestedSuppliers.length}</span></div>
          </div>
        </div>
      </div>

      {requestedSuppliers.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No supplier requests found. Go back to A50 and send at least one request first.
        </div>
      )}

      <div className="space-y-2">
        {requestedSuppliers.map(s => {
          const q = quotes[s.supplier_id] || {};
          const captured = !!(q.amount || q.job_ref);
          return (
            <div key={s.supplier_id} className={`p-3 border rounded-lg ${captured ? 'bg-teal-50 border-teal-300' : 'bg-white border-slate-200'}`} data-testid={`wf-quote-capture-row-${s.supplier_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{s.name}</div>
                  <div className="text-[10px] text-slate-500">{s.email}{s.sent_at && ` · Request sent ${new Date(s.sent_at).toLocaleDateString()}`}</div>
                </div>
                {captured && (
                  <span className="text-[10px] font-semibold text-teal-800 bg-teal-100 border border-teal-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                    <CheckCircle2 size={10} /> Captured
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Job Reference</label>
                  <input
                    type="text"
                    value={q.job_ref || ''}
                    onChange={(e) => updateQuote(s.supplier_id, { supplier_name: s.name, email: s.email, job_ref: e.target.value })}
                    placeholder="e.g. PP-2026-0432"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                    data-testid={`wf-quote-jobref-${s.supplier_id}`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5 flex items-center gap-1"><DollarSign size={10} /> Amount (NZD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={q.amount || ''}
                    onChange={(e) => updateQuote(s.supplier_id, { supplier_name: s.name, email: s.email, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g. 3250"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                    data-testid={`wf-quote-amount-${s.supplier_id}`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5 flex items-center gap-1"><Clock size={10} /> Downtime (days)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={q.downtime_days || ''}
                    onChange={(e) => updateQuote(s.supplier_id, { supplier_name: s.name, email: s.email, downtime_days: parseInt(e.target.value, 10) || 0 })}
                    placeholder="e.g. 3"
                    className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                    data-testid={`wf-quote-downtime-${s.supplier_id}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Notes</label>
                <input
                  type="text"
                  value={q.notes || ''}
                  onChange={(e) => updateQuote(s.supplier_id, { supplier_name: s.name, email: s.email, notes: e.target.value })}
                  placeholder="Any conditions or caveats they mentioned"
                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                  data-testid={`wf-quote-notes-${s.supplier_id}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-quote-capture-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-quote-capture-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!valid}
            className="px-4 py-1.5 rounded-md text-sm bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-quote-capture-continue"
            title={valid ? 'Continue to pick the winning supplier' : 'Capture at least one supplier quote to continue'}
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
